/**
 * Иностранный посетитель astrofeel.ru — по часовому поясу браузера.
 *
 * Признак — Intl.DateTimeFormat().resolvedOptions().timeZone: сайт уже
 * берёт его для профиля (lib/auth.js → POST /auth/timezone) и расчётов.
 * Пояс не из списка России — иностранец. Пояса нет (старый браузер) или он
 * служебный (UTC, Etc/…) — считаем, что из России: так всё остаётся, как было.
 *
 * Где включено: только ру (IS_ROBOKASSA) и только при включённом Stripe
 * (REACT_APP_STRIPE_ENABLED). На астрофил.рф обе переменные по умолчанию,
 * STRIPE_PRIMARY там всегда false, и ничего не меняется.
 *
 * Пара на бэкенде — stripe_one_time.py (KZ_TIMEZONES): цену разовой покупки
 * считает сервер по тому же поясу, что пришёл из браузера.
 */
import { IS_ROBOKASSA, STRIPE_ENABLED } from '@/lib/siteConfig';

// Все пояса России в базе IANA (tzdata), включая старый псевдоним W-SU.
export const RU_TIMEZONES = new Set([
  'Europe/Kaliningrad', 'Europe/Moscow', 'Europe/Simferopol', 'Europe/Kirov',
  'Europe/Volgograd', 'Europe/Astrakhan', 'Europe/Saratov', 'Europe/Ulyanovsk',
  'Europe/Samara', 'Asia/Yekaterinburg', 'Asia/Omsk', 'Asia/Novosibirsk',
  'Asia/Barnaul', 'Asia/Tomsk', 'Asia/Novokuznetsk', 'Asia/Krasnoyarsk',
  'Asia/Irkutsk', 'Asia/Chita', 'Asia/Yakutsk', 'Asia/Khandyga',
  'Asia/Vladivostok', 'Asia/Ust-Nera', 'Asia/Magadan', 'Asia/Sakhalin',
  'Asia/Srednekolymsk', 'Asia/Kamchatka', 'Asia/Anadyr', 'W-SU',
]);

// Казахстан — свой прайс в тенге. Совпадает с backend stripe_one_time.KZ_TIMEZONES.
export const KZ_TIMEZONES = new Set([
  'Asia/Almaty', 'Asia/Qostanay', 'Asia/Aqtobe', 'Asia/Aqtau',
  'Asia/Atyrau', 'Asia/Oral', 'Asia/Qyzylorda',
]);

export function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (_e) {
    return '';
  }
}

/** Пояс не российский и не служебный — иностранец. */
export function isForeignTimeZone(tz) {
  const z = String(tz || '').trim();
  if (!z || z === 'UTC' || z.startsWith('Etc/')) return false;
  return !RU_TIMEZONES.has(z);
}

/** Цена разовой покупки для иностранца: Казахстан — 1 190 ₸, остальные — 2,49 $.
 * Вид — как у цен ПРО (formatStripeAmount): сумма, неразрывный пробел, знак. */
export function foreignOneTimePrice(tz = browserTimeZone()) {
  return KZ_TIMEZONES.has(String(tz || '').trim())
    ? { currency: 'KZT', amount: 1190, label: '1 190 ₸' }
    : { currency: 'USD', amount: 2.49, label: '2,49\u00A0$' };
}

export const VISITOR_TIMEZONE = browserTimeZone();

/**
 * Валюта по умолчанию в окне Stripe (тарифы ПРО): тот же признак региона,
 * что у цены разовой покупки — Казахстан → KZT, остальные → USD. Прежняя
 * память браузера и угадывание сервера сюда не подмешиваются; другую
 * валюту человек выбирает в самом окне.
 */
export function stripeDefaultCurrency(tz = VISITOR_TIMEZONE) {
  return KZ_TIMEZONES.has(String(tz || '').trim()) ? 'KZT' : 'USD';
}

/**
 * Окно «Оплата иностранной картой» с выбором валюты не нужно, когда валюта
 * посетителя уже определена как тенге: кнопка ПРО сразу ведёт в Stripe в
 * тенге (07.10.2026). Остальным иностранцам окно остаётся.
 */
export function stripeSkipsCurrencyModal(tz = VISITOR_TIMEZONE) {
  return stripeDefaultCurrency(tz) === 'KZT';
}

/**
 * Главная касса — Stripe: ру, Stripe включён, посетитель — иностранец.
 * На рф всегда false.
 */
export function stripePrimaryFor(tz, isRobokassa = IS_ROBOKASSA, stripeEnabled = STRIPE_ENABLED) {
  return !!(isRobokassa && stripeEnabled && isForeignTimeZone(tz));
}

export const STRIPE_PRIMARY = stripePrimaryFor(VISITOR_TIMEZONE);
